// Librairies
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';

// Services
import { AttachmentService } from '../api/attachments/attachment.service';
import { LocalStorageService } from './local-storage.service';

// Classes
import { WebmStitcher } from '../../classes/core/webm-stitcher';

// Interfaces
import { Attachment } from '../../interfaces/api/attachment';
import { Video, VideoRecordingStart, VideoRecordingStatus } from '../../interfaces/api/video';

export interface RecordingMeta extends Omit<VideoRecordingStart, 'uploadId' | 'path'> {
  trackerName?: string;
}

interface StoredRecording {
  uploadId: string;
  owner: string;
  db: string;
  meta: RecordingMeta;
  thumbnail: string;
  written: number;
  uploaded: number;
  started: boolean;
  stopped: boolean;
  touchedAt: number;
  lastTimecode: number;
  lastTimecodeAt?: number;
  sent?: number;
  batch?: { index: number; end: number };
  full?: boolean;
}

interface StoredChunk {
  uploadId: string;
  index: number;
  blob: Blob;
}

export interface RecordingProgress {
  uploadId: string;
  uploaded: number;
  written: number;
  stopped: boolean;
}

export interface RecordingDone {
  uploadId: string;
  meta: RecordingMeta;
  attachment: Attachment;
  video: Video;
}

const DB_NAME = 'unifield-recordings';
const RECORDINGS = 'recordings';
const CHUNKS = 'chunks';
const FLUSH_BYTES = 2 * 1024 * 1024;
const FLUSH_MS = 1000;
const MAX_BACKOFF_MS = 30000;
const LOW_SPACE_BYTES = 500 * 1024 * 1024;
const ORPHAN_AFTER_MS = 15 * 60 * 1000;
const BATCH_BYTES = 8 * 1024 * 1024;
const BATCH_SCAN = 64;
const ROTATE_AT_CHUNKS = 49000;

@Injectable({
  providedIn: 'root'
})
export class RecordingService {

  // Variables
  readonly progress$ = new Subject<RecordingProgress>();
  readonly done$ = new Subject<RecordingDone>();
  readonly dropped$ = new Subject<{ uploadId: string; reason: string }>();
  readonly lowSpace$ = new Subject<number>();
  readonly full$ = new Subject<string>();

  private dbPromise?: Promise<IDBDatabase>;
  private buffers = new Map<string, { parts: Blob[]; bytes: number; timer: any; pending: Promise<void>; lastTimecode: number; lastTimecodeAt: number }>();
  private closing = new Set<string>();
  private pumping = new Set<string>();
  private rerun = new Set<string>();
  private backoff = new Map<string, number>();
  private wakeTimers = new Map<string, any>();

  constructor(private attachmentService: AttachmentService, private localStorage: LocalStorageService) {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.wakeAll());
    }
  }

  get supported(): boolean {
    return typeof indexedDB !== 'undefined';
  }

  async begin(uploadId: string, owner: string, meta: RecordingMeta, thumbnail: string): Promise<void> {
    const db = sessionStorage.getItem('db') || this.localStorage.getItem<string>('db') || '';
    await this.persistStorage();
    const record: StoredRecording = { uploadId, owner, db, meta, thumbnail, written: 0, uploaded: 0, started: false, stopped: false, touchedAt: Date.now(), lastTimecode: -1 };
    await this.tx([RECORDINGS], 'readwrite', (t) => t.objectStore(RECORDINGS).put(record));
    await this.flush(uploadId);
    this.kick(uploadId);
    void this.checkSpace();
  }

  async reopen(uploadId: string): Promise<void> {
    await this.update(uploadId, (r) => { r.stopped = false; r.touchedAt = Date.now(); });
    this.kick(uploadId);
  }

  append(uploadId: string, blob: Blob, stitcher?: WebmStitcher): void {
    if (!blob.size) return;
    const buffer = this.buffer(uploadId);
    buffer.pending = buffer.pending.then(async () => {
      const data = stitcher ? new Blob([stitcher.push(new Uint8Array(await blob.arrayBuffer())) as BlobPart]) : blob;
      if (stitcher && stitcher.lastTimecode > buffer.lastTimecode) {
        buffer.lastTimecode = stitcher.lastTimecode;
        buffer.lastTimecodeAt = Date.now();
      }
      if (!data.size) return;
      buffer.parts.push(data);
      buffer.bytes += data.size;
      if (buffer.bytes >= FLUSH_BYTES) {
        void this.flush(uploadId);
      } else if (!buffer.timer) {
        buffer.timer = setTimeout(() => void this.flush(uploadId), FLUSH_MS);
      }
    }).catch((err) => console.error('[recording] could not process a piece:', err));
  }

  pause(uploadId: string): Promise<void> {
    return this.flush(uploadId);
  }

  async findOpen(owner: string, eventId?: string): Promise<StoredRecording | undefined> {
    if (!this.supported) return undefined;
    const records = await this.all();
    return records
      .filter((r) => r.owner === owner && !r.stopped && !r.full && !this.closing.has(r.uploadId) && r.meta.event === eventId)
      .sort((a, b) => b.touchedAt - a.touchedAt)[0];
  }

  markClosing(uploadId: string): void {
    this.closing.add(uploadId);
  }

  async end(uploadId: string): Promise<void> {
    this.closing.add(uploadId);
    await this.flush(uploadId);
    this.buffers.delete(uploadId);
    await this.update(uploadId, (r) => { r.stopped = true; });
    this.kick(uploadId);
  }

  async resume(owner: string): Promise<RecordingProgress[]> {
    if (!this.supported) return [];
    const records = await this.all();
    const mine = records.filter((r) => r.owner === owner && !this.buffers.has(r.uploadId));
    for (const r of mine) {
      if (!r.stopped && (r.full || Date.now() - (r.touchedAt ?? 0) > ORPHAN_AFTER_MS)) {
        await this.update(r.uploadId, (x) => { x.stopped = true; });
      }
      this.kick(r.uploadId);
    }
    return mine.map((r) => ({ uploadId: r.uploadId, uploaded: r.uploaded, written: r.written, stopped: r.stopped }));
  }

  async get(uploadId: string): Promise<StoredRecording | undefined> {
    return this.tx([RECORDINGS], 'readonly', (t) => t.objectStore(RECORDINGS).get(uploadId));
  }

  private buffer(uploadId: string) {
    let buffer = this.buffers.get(uploadId);
    if (!buffer) {
      buffer = { parts: [], bytes: 0, timer: null, pending: Promise.resolve(), lastTimecode: -1, lastTimecodeAt: 0 };
      this.buffers.set(uploadId, buffer);
    }
    return buffer;
  }

  private flush(uploadId: string): Promise<void> {
    const buffer = this.buffers.get(uploadId);
    if (!buffer) return Promise.resolve();
    clearTimeout(buffer.timer);
    buffer.timer = null;
    buffer.pending = buffer.pending.then(async () => {
      if (!buffer.parts.length) return;
      if (!(await this.get(uploadId))) return;
      const blob = new Blob(buffer.parts, { type: 'video/webm' });
      buffer.parts = [];
      buffer.bytes = 0;
      try {
        await this.writeChunk(uploadId, blob, buffer.lastTimecode, buffer.lastTimecodeAt);
      } catch (err) {
        console.error('[recording] could not store a piece locally:', err);
        void this.checkSpace(true);
        return;
      }
      this.kick(uploadId);
    }).catch((err) => console.error('[recording] flush failed:', err));
    return buffer.pending;
  }

  private async writeChunk(uploadId: string, blob: Blob, lastTimecode: number, lastTimecodeAt: number): Promise<void> {
    const db = await this.open();
    let full = false;
    await new Promise<void>((resolve, reject) => {
      const t = db.transaction([RECORDINGS, CHUNKS], 'readwrite');
      const recordings = t.objectStore(RECORDINGS);
      const req = recordings.get(uploadId);
      req.onsuccess = () => {
        const record = req.result as StoredRecording | undefined;
        if (!record) return;
        t.objectStore(CHUNKS).put({ uploadId, index: record.written, blob } as StoredChunk);
        record.written++;
        record.touchedAt = Date.now();
        if (lastTimecode > (record.lastTimecode ?? -1)) {
          record.lastTimecode = lastTimecode;
          record.lastTimecodeAt = lastTimecodeAt;
        }
        if (!record.full && record.written >= ROTATE_AT_CHUNKS) record.full = full = true;
        recordings.put(record);
      };
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
    if (full) this.full$.next(uploadId);
  }

  private kick(uploadId: string) {
    clearTimeout(this.wakeTimers.get(uploadId));
    this.wakeTimers.delete(uploadId);
    if (this.pumping.has(uploadId)) {
      this.rerun.add(uploadId);
      return;
    }
    void this.pump(uploadId);
  }

  private wakeAll() {
    for (const uploadId of [...this.wakeTimers.keys()]) {
      this.backoff.delete(uploadId);
      this.kick(uploadId);
    }
  }

  private async pump(uploadId: string): Promise<void> {
    this.pumping.add(uploadId);
    try {
      do {
        this.rerun.delete(uploadId);
        await this.drain(uploadId);
        this.backoff.delete(uploadId);
      } while (this.rerun.has(uploadId));
    } catch (err) {
      if (this.isRetryable(err)) {
        const delay = Math.min(MAX_BACKOFF_MS, (this.backoff.get(uploadId) ?? 1000) * 2);
        this.backoff.set(uploadId, delay);
        this.wakeTimers.set(uploadId, setTimeout(() => this.kick(uploadId), delay));
      } else {
        console.error('[recording] upload stopped, will retry on next load:', err);
      }
    } finally {
      this.pumping.delete(uploadId);
    }
  }

  private async drain(uploadId: string): Promise<void> {
    let record = await this.get(uploadId);
    if (!record) return;

    if (!record.started) {
      let status: VideoRecordingStatus;
      try {
        status = await firstValueFrom(this.attachmentService.startVideoRecording(
          { uploadId, ...record.meta, path: record.thumbnail }, record.db,
        ));
      } catch (err) {
        if (err instanceof HttpErrorResponse && err.status >= 400 && err.status < 500 && !this.isRetryable(err)) {
          console.error('[recording] the server refused the recording:', err.error);
          return this.drop(uploadId, err.error?.error ?? `http_${err.status}`);
        }
        throw err;
      }
      if (status.status === 'closing') throw new Error('recording closing');
      if (status.status !== 'open') return this.drop(uploadId, status.status);
      record = await this.update(uploadId, (r) => { r.started = true; this.reconcile(r, status.nextIndex); });
      await this.deleteChunksBelow(uploadId, record.uploaded);
    }

    while (record.uploaded < record.written) {
      const index = this.sentOf(record);
      const limit = record.batch?.index === index ? Math.min(record.batch.end, record.written) : record.written;
      const blobs = await this.nextBatch(uploadId, record.uploaded, limit);
      if (!blobs.length) {
        console.warn('[recording] piece missing locally, closing at', record.uploaded);
        record = await this.update(uploadId, (r) => { r.written = r.uploaded; r.stopped = true; });
        break;
      }
      const end = record.uploaded + blobs.length;
      record = await this.update(uploadId, (r) => { r.batch = { index, end }; });
      try {
        await firstValueFrom(this.attachmentService.postVideoRecordingChunk(uploadId, index, new Blob(blobs, { type: 'video/webm' }), record.db));
        record = await this.update(uploadId, (r) => { r.uploaded = end; r.sent = index + 1; r.batch = undefined; });
      } catch (err) {
        if (this.forbidden(err)) return this.drop(uploadId, 'forbidden');
        const error = this.conflict(err);
        if (error?.error === 'recording_finalized') return this.drop(uploadId, 'finalized');
        if (error?.error !== 'chunk_gap' || typeof error.nextIndex !== 'number') throw err;
        const nextIndex = error.nextIndex;
        record = await this.update(uploadId, (r) => this.reconcile(r, nextIndex));
      }
      await this.deleteChunksBelow(uploadId, record.uploaded);
      this.progress$.next({ uploadId, uploaded: record.uploaded, written: record.written, stopped: record.stopped });
    }

    if (!record.stopped) return;
    if (!record.written) return this.drop(uploadId, 'empty');

    try {
      const result = await firstValueFrom(this.attachmentService.completeVideoRecording(
        { uploadId, chunkCount: this.sentOf(record) }, record.db,
      ));
      await this.remove(uploadId);
      this.done$.next({ uploadId, meta: record.meta, attachment: result.attachment, video: result.video });
    } catch (err) {
      if (this.forbidden(err)) return this.drop(uploadId, 'forbidden');
      const error = this.conflict(err);
      if (error?.error === 'recording_finalized') return this.drop(uploadId, 'finalized');
      if (error?.error === 'recording_abandoned') return this.drop(uploadId, 'abandoned');
      if (error?.error === 'recording_closing') throw new Error('recording closing');
      if (error?.error !== 'missing_chunks' || typeof error.nextIndex !== 'number') throw err;
      const nextIndex = error.nextIndex;
      console.warn('[recording] the server is missing pieces, completing with', nextIndex);
      await this.update(uploadId, (r) => this.reconcile(r, nextIndex));
      this.rerun.add(uploadId);
    }
  }

  private sentOf(record: StoredRecording): number {
    return record.sent ?? record.uploaded;
  }

  private reconcile(record: StoredRecording, nextIndex: number) {
    const sent = this.sentOf(record);
    if (nextIndex > sent) {
      record.uploaded = record.batch?.index === sent ? record.batch.end : Math.max(record.uploaded, nextIndex);
    }
    record.sent = nextIndex;
    record.batch = undefined;
  }

  private async nextBatch(uploadId: string, from: number, to: number): Promise<Blob[]> {
    const range = IDBKeyRange.bound([uploadId, from], [uploadId, to], false, true);
    const chunks = await this.tx<StoredChunk[]>([CHUNKS], 'readonly', (t) => t.objectStore(CHUNKS).getAll(range, BATCH_SCAN));
    const blobs: Blob[] = [];
    let bytes = 0;
    for (const chunk of chunks) {
      if (chunk.index !== from + blobs.length) break;
      if (blobs.length && bytes + chunk.blob.size > BATCH_BYTES) break;
      blobs.push(chunk.blob);
      bytes += chunk.blob.size;
    }
    return blobs;
  }

  private forbidden(err: unknown): boolean {
    return err instanceof HttpErrorResponse && err.status === 403;
  }

  private conflict(err: unknown): { error?: string; nextIndex?: number } | undefined {
    if (err instanceof HttpErrorResponse && err.status === 409) return err.error ?? {};
    return undefined;
  }

  private isRetryable(err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse)) return true;
    return err.status === 0 || err.status === 408 || err.status === 429 || err.status >= 500;
  }

  private async drop(uploadId: string, reason: string): Promise<void> {
    await this.remove(uploadId);
    this.dropped$.next({ uploadId, reason });
  }

  private async remove(uploadId: string): Promise<void> {
    this.buffers.delete(uploadId);
    this.closing.delete(uploadId);
    await this.deleteChunksBelow(uploadId, Number.MAX_SAFE_INTEGER);
    await this.tx([RECORDINGS], 'readwrite', (t) => t.objectStore(RECORDINGS).delete(uploadId));
  }

  private deleteChunksBelow(uploadId: string, index: number): Promise<undefined> {
    return this.tx([CHUNKS], 'readwrite', (t) => t.objectStore(CHUNKS).delete(IDBKeyRange.bound([uploadId, 0], [uploadId, index], false, true)));
  }

  private async update(uploadId: string, change: (r: StoredRecording) => void): Promise<StoredRecording> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const t = db.transaction([RECORDINGS], 'readwrite');
      const store = t.objectStore(RECORDINGS);
      let record: StoredRecording | undefined;
      const req = store.get(uploadId);
      req.onsuccess = () => {
        record = req.result;
        if (!record) return;
        change(record);
        store.put(record);
      };
      t.oncomplete = () => record ? resolve(record) : reject(new Error(`recording ${uploadId} not found`));
      t.onerror = () => reject(t.error);
    });
  }

  private all(): Promise<StoredRecording[]> {
    return this.tx([RECORDINGS], 'readonly', (t) => t.objectStore(RECORDINGS).getAll());
  }

  private async tx<T>(stores: string[], mode: IDBTransactionMode, run: (t: IDBTransaction) => IDBRequest<T>): Promise<T> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const t = db.transaction(stores, mode);
      const req = run(t);
      t.oncomplete = () => resolve(req.result);
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
  }

  private open(): Promise<IDBDatabase> {
    this.dbPromise ??= new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(RECORDINGS)) db.createObjectStore(RECORDINGS, { keyPath: 'uploadId' });
        if (!db.objectStoreNames.contains(CHUNKS)) db.createObjectStore(CHUNKS, { keyPath: ['uploadId', 'index'] });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        this.dbPromise = undefined;
        reject(req.error);
      };
    });
    return this.dbPromise;
  }

  private async persistStorage(): Promise<void> {
    try {
      if (navigator.storage?.persist && !(await navigator.storage.persisted())) await navigator.storage.persist();
    } catch {}
  }

  private async checkSpace(force = false): Promise<void> {
    try {
      const estimate = await navigator.storage?.estimate?.();
      if (!estimate?.quota) return;
      const free = estimate.quota - (estimate.usage ?? 0);
      if (force || free < LOW_SPACE_BYTES) this.lowSpace$.next(free);
    } catch {}
  }
}
